import { z } from 'zod';
import type { FieldErrors } from 'react-hook-form';
import {
  Calendar,
  Camera,
  CheckCheck,
  Gauge,
  List,
  type LucideIcon,
  PenLine,
  Ruler,
  ScanLine,
  Signature,
  SquareCheck,
  ThumbsUp,
} from 'lucide-react';
import type {
  ChecklistInstanceSection,
  ChecklistItemConfig,
  ChecklistItemType,
  ChecklistJobTypeLink,
  ChecklistTemplate,
  CreateChecklistTemplateDto,
  UpdateChecklistTemplateDto,
} from '@/types/settings';

export const ITEM_TYPES: { value: ChecklistItemType; label: string; hint: string }[] = [
  { value: 'CHECKBOX', label: 'Checkbox', hint: 'Tick to confirm' },
  { value: 'YES_NO', label: 'Yes / No', hint: 'Two-option toggle' },
  { value: 'PASS_FAIL', label: 'Pass / Fail', hint: 'Red/green toggle' },
  { value: 'TEXT', label: 'Text', hint: 'Free-form input' },
  { value: 'NUMBER', label: 'Number', hint: 'Numeric value with optional unit' },
  { value: 'MEASUREMENT', label: 'Measurement', hint: 'Value + unit picker' },
  { value: 'DROPDOWN', label: 'Dropdown', hint: 'Pick one of a list' },
  { value: 'DATE', label: 'Date', hint: 'Date picker' },
  { value: 'PHOTO', label: 'Photo', hint: 'Capture one or more photos' },
  { value: 'SIGNATURE', label: 'Signature', hint: 'Signature capture' },
  { value: 'BARCODE_SCAN', label: 'Barcode', hint: 'Scan a code' },
];

export const ITEM_TYPE_LABEL: Record<ChecklistItemType, string> = Object.fromEntries(
  ITEM_TYPES.map((type) => [type.value, type.label]),
) as Record<ChecklistItemType, string>;

/**
 * Plain-language presentation layer for the builder v2 "list of checks" UI
 * (NOT-208). Maps each `ChecklistItemType` to the criollo label the owner sees
 * ("Tick it off", "Take a photo"…) + a short hint + a lucide icon. `ITEM_TYPES`
 * above stays the internal mapping; this is purely how a check's answer type is
 * shown on the line and in the answer-type popover. No model change.
 *
 * NUMBER vs MEASUREMENT share one popover entry ("Number or reading"); the
 * "Let them pick a unit" toggle inside "More" is what flips between them.
 */
export const ANSWER_TYPES: Record<
  ChecklistItemType,
  { label: string; hint?: string; icon: LucideIcon }
> = {
  CHECKBOX: { label: 'Tick it off', hint: 'A simple done / not done', icon: SquareCheck },
  YES_NO: { label: 'Yes or no', hint: 'A two-way answer', icon: ThumbsUp },
  PASS_FAIL: { label: 'Pass or fail', hint: 'Passed or failed the check', icon: CheckCheck },
  PHOTO: { label: 'Take a photo', hint: 'The photo is the answer', icon: Camera },
  DROPDOWN: { label: 'Pick from a list', hint: 'Choose one of your choices', icon: List },
  TEXT: { label: 'Write a note', hint: 'Type a short answer', icon: PenLine },
  NUMBER: { label: 'Number or reading', hint: 'A number, with an optional unit', icon: Ruler },
  MEASUREMENT: { label: 'Number or reading', hint: 'A number, with a unit', icon: Gauge },
  DATE: { label: 'Date', hint: 'Pick a date', icon: Calendar },
  SIGNATURE: { label: 'Signature', hint: 'Sign to confirm', icon: Signature },
  BARCODE_SCAN: { label: 'Scan a barcode', hint: 'Scan a code', icon: ScanLine },
};

/** Answer types shown first in the popover (common), in display order. */
export const COMMON_ANSWER_TYPES: ChecklistItemType[] = [
  'CHECKBOX',
  'YES_NO',
  'PASS_FAIL',
  'PHOTO',
  'DROPDOWN',
  'TEXT',
];

/** Answer types shown under "More answer types", in display order. */
export const MORE_ANSWER_TYPES: ChecklistItemType[] = [
  'NUMBER',
  'DATE',
  'SIGNATURE',
  'BARCODE_SCAN',
];

const itemSchema = z
  .object({
    id: z.string().optional(),
    placementKey: z.string().optional(),
    questionId: z.string().uuid().optional(),
    questionVersionId: z.string().uuid().optional(),
    label: z.string().min(1, 'Label is required').max(500),
    type: z.enum([
      'CHECKBOX',
      'YES_NO',
      'PASS_FAIL',
      'TEXT',
      'NUMBER',
      'PHOTO',
      'SIGNATURE',
      'DROPDOWN',
      'DATE',
      'BARCODE_SCAN',
      'MEASUREMENT',
    ]),
    required: z.boolean().default(false),
    description: z.string().max(1000).optional(),
    photoRequired: z.boolean().default(false),
    minPhotos: z.number().int().min(1).max(20).default(1),
    maxPhotos: z.number().int().min(1).max(100).optional(),
    numberMin: z.string().optional(),
    numberMax: z.string().optional(),
    unit: z.string().optional(),
    unitsCsv: z.string().optional(),
    options: z.array(z.object({ value: z.string().max(200) })).optional(),
    signerRole: z.string().optional(),
    maxLength: z.string().optional(),
    allowPast: z.boolean().default(true),
    allowFuture: z.boolean().default(true),
    barcodeFormat: z.enum(['ANY', 'UPC', 'QR', 'CODE128']).default('ANY'),
  })
  .superRefine((val, ctx) => {
    if (val.type === 'DROPDOWN') {
      const opts = (val.options ?? []).map((o) => o.value.trim()).filter(Boolean);
      if (opts.length < 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least 2 options required',
          path: ['options'],
        });
      }
    }

    if (val.type === 'MEASUREMENT') {
      const units = csvTo(val.unitsCsv);
      if (units.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'At least 1 unit required',
          path: ['unitsCsv'],
        });
      }
    }

    if (val.type === 'NUMBER' || val.type === 'MEASUREMENT') {
      const min = val.numberMin?.trim() ? Number(val.numberMin) : undefined;
      const max = val.numberMax?.trim() ? Number(val.numberMax) : undefined;

      if (min !== undefined && !Number.isFinite(min)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Min must be a valid number',
          path: ['numberMin'],
        });
      }

      if (max !== undefined && !Number.isFinite(max)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Max must be a valid number',
          path: ['numberMax'],
        });
      }

      if (min !== undefined && max !== undefined && Number.isFinite(min) && Number.isFinite(max)) {
        if (min >= max) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Min must be less than max',
            path: ['numberMin'],
          });
        }
      }
    }

    if (val.type === 'TEXT' && val.maxLength?.trim()) {
      const maxLength = Number(val.maxLength);
      if (!Number.isInteger(maxLength) || maxLength < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Max length must be a positive whole number',
          path: ['maxLength'],
        });
      }
    }

    if (val.type === 'DATE' && !val.allowPast && !val.allowFuture) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Allow past dates or future dates',
        path: ['allowPast'],
      });
    }

    if (val.type === 'PHOTO' && val.maxPhotos != null && val.maxPhotos < val.minPhotos) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Max photos must be greater than or equal to min photos',
        path: ['maxPhotos'],
      });
    }
  });

const sectionSchema = z
  .object({
    id: z.string().optional(),
    name: z.string().min(1, 'Section name is required').max(255),
    isRepeatable: z.boolean().default(false),
    repeatOptions: z.array(z.object({ value: z.string().max(200) })).optional(),
    items: z.array(itemSchema).min(1, 'At least one item required'),
  })
  .superRefine((val, ctx) => {
    if (!val.isRepeatable) return;

    const opts = (val.repeatOptions ?? []).map((o) => o.value.trim()).filter(Boolean);
    if (opts.length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least 1 repeat option required',
        path: ['repeatOptions'],
      });
    }
  });

export const templateSchema = z.object({
  name: z.string().min(1, 'Name is required').max(200),
  description: z.string().max(1000).optional(),
  defaultVisibility: z.enum(['INTERNAL', 'CUSTOMER_VISIBLE']).default('INTERNAL'),
  sections: z.array(sectionSchema).min(1, 'At least one section required'),
});

export type TemplateFormValues = z.input<typeof templateSchema>;

function csvFrom(arr?: string[]): string {
  return (arr ?? []).join(', ');
}

function csvTo(s: string | undefined): string[] {
  return (s ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
}

export function defaultsFromTemplate(tpl: ChecklistTemplate | null): TemplateFormValues {
  if (!tpl) {
    return {
      name: '',
      description: '',
      defaultVisibility: 'INTERNAL',
      sections: [
        {
          name: 'General',
          isRepeatable: false,
          repeatOptions: [],
          items: [
            {
              label: '',
              type: 'CHECKBOX',
              required: false,
              description: '',
              photoRequired: false,
              minPhotos: 1,
              maxPhotos: undefined,
              options: [],
              allowPast: true,
              allowFuture: true,
              barcodeFormat: 'ANY',
            },
          ],
        },
      ],
    };
  }

  return {
    name: tpl.name,
    description: tpl.description ?? '',
    defaultVisibility: tpl.defaultVisibility ?? 'INTERNAL',
    sections: tpl.sections.map((section) => ({
      id: section.id,
      name: section.name,
      isRepeatable: !!section.isRepeatable,
      repeatOptions: (section.repeatOptions ?? []).map((value) => ({ value })),
      items: section.items.map((item) => ({
        id: item.id,
        placementKey: item.placementKey,
        questionId: item.questionId,
        questionVersionId: item.questionVersionId,
        label: item.label,
        type: item.type,
        required: item.required,
        description: item.description ?? '',
        photoRequired: !!item.photoRequired,
        minPhotos: item.config?.minPhotos ?? item.minPhotos ?? 1,
        maxPhotos: item.config?.maxPhotos,
        numberMin: item.config?.min != null ? String(item.config.min) : '',
        numberMax: item.config?.max != null ? String(item.config.max) : '',
        unit: item.config?.unit ?? '',
        unitsCsv: csvFrom(item.config?.units),
        options: (item.config?.options ?? []).map((value) => ({ value })),
        signerRole: item.config?.signerRole ?? '',
        maxLength: item.config?.maxLength != null ? String(item.config.maxLength) : '',
        allowPast: item.config?.allowPast ?? true,
        allowFuture: item.config?.allowFuture ?? true,
        barcodeFormat: item.config?.format ?? 'ANY',
      })),
    })),
  };
}

export function buildItemConfig(item: TemplateFormValues['sections'][number]['items'][number]) {
  const config: Record<string, unknown> = {};

  switch (item.type) {
    case 'NUMBER':
      if (item.numberMin) config.min = Number(item.numberMin);
      if (item.numberMax) config.max = Number(item.numberMax);
      if (item.unit) config.unit = item.unit;
      break;
    case 'MEASUREMENT':
      if (item.numberMin) config.min = Number(item.numberMin);
      if (item.numberMax) config.max = Number(item.numberMax);
      config.units = csvTo(item.unitsCsv);
      break;
    case 'DROPDOWN':
      config.options = (item.options ?? []).map((o) => o.value.trim()).filter(Boolean);
      break;
    case 'SIGNATURE':
      if (item.signerRole) config.signerRole = item.signerRole;
      break;
    case 'TEXT':
      if (item.maxLength) config.maxLength = Number(item.maxLength);
      break;
    case 'PHOTO':
      if (item.minPhotos) config.minPhotos = item.minPhotos;
      if (item.maxPhotos) config.maxPhotos = item.maxPhotos;
      break;
    case 'DATE':
      config.allowPast = item.allowPast ?? true;
      config.allowFuture = item.allowFuture ?? true;
      break;
    case 'BARCODE_SCAN':
      config.format = item.barcodeFormat ?? 'ANY';
      break;
  }

  return Object.keys(config).length ? config : undefined;
}

export type SelectedJobTypeLink = Required<Pick<ChecklistJobTypeLink, 'jobTypeId'>> &
  Pick<ChecklistJobTypeLink, 'blocksCompletion' | 'sortOrder'>;

export function linksFromTemplate(template: ChecklistTemplate): SelectedJobTypeLink[] {
  const persistedLinks = template.jobTypeLinks ?? [];
  return persistedLinks.map((link, index) => ({
    jobTypeId: link.jobTypeId,
    blocksCompletion: link.blocksCompletion ?? false,
    sortOrder: link.sortOrder ?? index,
  }));
}

export function dtoFromFormValues(
  values: TemplateFormValues,
  selectedJobTypeLinks: SelectedJobTypeLink[],
): CreateChecklistTemplateDto {
  return {
    name: values.name,
    description: values.description || undefined,
    defaultVisibility: values.defaultVisibility,
    jobTypeLinks: selectedJobTypeLinks.map((link, index) => ({
      jobTypeId: link.jobTypeId,
      blocksCompletion: link.blocksCompletion ?? false,
      sortOrder: link.sortOrder ?? index,
    })),
    sections: values.sections.map((section, sectionIndex) => ({
      id: section.id,
      name: section.name,
      order: sectionIndex,
      isRepeatable: section.isRepeatable,
      repeatOptions: section.isRepeatable
        ? (section.repeatOptions ?? []).map((o) => o.value.trim()).filter(Boolean)
        : undefined,
      items: section.items.map((item) => ({
        id: item.id,
        placementKey: item.placementKey,
        questionId: item.questionId,
        questionVersionId: item.questionVersionId,
        label: item.label,
        type: item.type,
        required: item.required,
        description: item.description || undefined,
        photoRequired: item.photoRequired,
        minPhotos: item.photoRequired ? item.minPhotos : undefined,
        config: buildItemConfig(item),
      })),
    })),
  };
}

function stableSections(sections: CreateChecklistTemplateDto['sections']) {
  return sections.map((section) => ({
    ...section,
    repeatOptions: section.repeatOptions ?? [],
    items: section.items.map((item) => ({
      ...item,
      required: item.required ?? false,
      description: item.description ?? undefined,
      photoRequired: item.photoRequired ?? false,
      minPhotos: item.minPhotos ?? undefined,
      config: item.config ?? undefined,
    })),
  }));
}

export function buildUpdateDtoFromFormValues(
  values: TemplateFormValues,
  selectedJobTypeLinks: SelectedJobTypeLink[],
  template: ChecklistTemplate,
): UpdateChecklistTemplateDto {
  const dto: UpdateChecklistTemplateDto = dtoFromFormValues(values, selectedJobTypeLinks);
  const currentSections = dtoFromFormValues(
    defaultsFromTemplate(template),
    linksFromTemplate(template),
  ).sections;
  const nextSections = dto.sections ?? [];

  if (
    JSON.stringify(stableSections(nextSections)) === JSON.stringify(stableSections(currentSections))
  ) {
    delete dto.sections;
  }

  return dto;
}

export interface ChecklistTemplateIssue {
  /** e.g. "sections.0.items.1.label" — used for stable keys / debugging. */
  path: string;
  /** The section the issue lives in, so the UI can expand it. */
  sectionIndex?: number;
  /** Human-readable message with location, e.g. 'Section 1 → item 1: …'. */
  message: string;
}

/**
 * RHF nests per-chip errors (e.g. an overlong `options[i].value` /
 * `repeatOptions[i].value`) as an array where each element is `{ value: {...} }`.
 * Pull those out so they reach the NOT-209 banner/toast instead of silently
 * blocking save — this also covers values loaded from an existing template that
 * predate the 200-char cap, which `maxLength` on new input can't fix.
 */
function collectChipValueErrors(arrError: unknown): { index: number; message: string }[] {
  if (!Array.isArray(arrError)) return [];
  const out: { index: number; message: string }[] = [];
  (arrError as Array<{ value?: { message?: string } } | undefined>).forEach((entry, index) => {
    const message = entry?.value?.message;
    if (message) out.push({ index, message });
  });
  return out;
}

/**
 * Flatten the react-hook-form error tree into a located, human-readable list
 * (NOT-209). Generic — walks whatever item fields have errors, so it works
 * regardless of the dropdown options encoding (`options` post-NOT-208).
 */
export function collectTemplateIssues(
  errors: FieldErrors<TemplateFormValues>,
  values: TemplateFormValues,
): ChecklistTemplateIssue[] {
  const issues: ChecklistTemplateIssue[] = [];

  if (errors.name?.message) {
    issues.push({ path: 'name', message: `Checklist name: ${errors.name.message}` });
  }

  const sectionsRoot = errors.sections as
    | { root?: { message?: string }; message?: string }
    | undefined;
  const sectionsRootMsg = sectionsRoot?.root?.message ?? sectionsRoot?.message;
  if (sectionsRootMsg) issues.push({ path: 'sections', message: sectionsRootMsg });

  const sectionErrors = Array.isArray(errors.sections) ? errors.sections : [];
  sectionErrors.forEach((sErr, sIdx) => {
    if (!sErr) return;
    const sName = values.sections?.[sIdx]?.name?.trim();
    const where = `Section ${sIdx + 1}${sName ? ` “${sName}”` : ''}`;

    if (sErr.name?.message) {
      issues.push({
        path: `sections.${sIdx}.name`,
        sectionIndex: sIdx,
        message: `${where}: ${sErr.name.message}`,
      });
    }
    const repeatRoot = sErr.repeatOptions as
      | { root?: { message?: string }; message?: string }
      | undefined;
    const repeatMsg = repeatRoot?.root?.message ?? repeatRoot?.message;
    if (repeatMsg) {
      issues.push({
        path: `sections.${sIdx}.repeatOptions`,
        sectionIndex: sIdx,
        message: `${where}: ${repeatMsg}`,
      });
    }
    collectChipValueErrors(sErr.repeatOptions).forEach(({ index, message }) => {
      issues.push({
        path: `sections.${sIdx}.repeatOptions.${index}.value`,
        sectionIndex: sIdx,
        message: `${where} → repeat option ${index + 1}: ${message}`,
      });
    });

    const itemsRoot = sErr.items as { root?: { message?: string }; message?: string } | undefined;
    const itemsRootMsg = itemsRoot?.root?.message ?? itemsRoot?.message;
    if (itemsRootMsg) {
      issues.push({
        path: `sections.${sIdx}.items`,
        sectionIndex: sIdx,
        message: `${where}: ${itemsRootMsg}`,
      });
    }

    // RHF types nested array errors loosely; treat each item's errors as a flat
    // field→message map for summarisation.
    const itemErrors = (Array.isArray(sErr.items) ? sErr.items : []) as Array<
      Record<string, { message?: string } | undefined> | undefined
    >;
    itemErrors.forEach((iErr, iIdx) => {
      if (!iErr) return;
      const iLabel = values.sections?.[sIdx]?.items?.[iIdx]?.label?.trim();
      const iWhere = `${where} → item ${iIdx + 1}${iLabel ? ` “${iLabel}”` : ''}`;
      Object.entries(iErr).forEach(([key, val]) => {
        if (!val?.message) return;
        issues.push({
          path: `sections.${sIdx}.items.${iIdx}.${key}`,
          sectionIndex: sIdx,
          message: `${iWhere}: ${val.message}`,
        });
      });
      // `options` is a nested array, so its per-chip errors are missed by the
      // flat map above — pull them out explicitly.
      collectChipValueErrors(iErr.options).forEach(({ index, message }) => {
        issues.push({
          path: `sections.${sIdx}.items.${iIdx}.options.${index}.value`,
          sectionIndex: sIdx,
          message: `${iWhere} → choice ${index + 1}: ${message}`,
        });
      });
    });
  });

  return issues;
}

/**
 * NOT-210: map the in-progress form into read-only instance sections so the
 * builder can render the exact technician/mobile view (reusing ChecklistItemInput
 * + buildItemConfig) without persisting anything. Values are blank — the state a
 * tech sees before filling it in.
 */
export function previewSectionsFromForm(values: TemplateFormValues): ChecklistInstanceSection[] {
  return values.sections.map((section, sIdx) => ({
    sectionDefId: `preview-${sIdx}`,
    name: section.name || 'Untitled section',
    sortOrder: sIdx,
    repeatLabel: null,
    repeatIndex: null,
    items: section.items.map((item, iIdx) => ({
      itemDefId: `preview-${sIdx}-${iIdx}`,
      label: item.label || 'Untitled item',
      type: item.type,
      required: item.required ?? false,
      description: item.description || null,
      photoRequired: item.photoRequired ?? false,
      minPhotos: item.minPhotos,
      config: buildItemConfig(item) as unknown as ChecklistItemConfig,
      value: null,
      photos: [],
    })),
  }));
}
